import { lazy } from 'react';

// project imports
import Loadable from 'ui-component/Loadable';
import jwt from 'jwtservice/jwtService';
import { Navigate } from 'react-router';
import PermissionGuard from './PermissionGuard';

const MainLayout = Loadable(lazy(() => import('layout/MainLayout')));
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/Default')));
const AddISP = Loadable(lazy(() => import('views/isps/AddISP')));
const EditISP = Loadable(lazy(() => import('views/isps/EditISP')));
const ViewAllISPs = Loadable(lazy(() => import('views/isps/ViewAllISPs')));
const AddPackage = Loadable(lazy(() => import('views/isps/AddPackage')));
const AllPackages = Loadable(lazy(() => import('views/isps/AllPackages')));
const AddEntry = Loadable(lazy(() => import('views/entries/AddEntry')));
const EditEntry = Loadable(lazy(() => import('views/entries/EditEntry')));
const AllEntries = Loadable(lazy(() => import('views/entries/AllEntries')));
const AddUser = Loadable(lazy(() => import('views/users/AddUser')));
const EditUser = Loadable(lazy(() => import('views/users/EditUser')));
const AllUsers = Loadable(lazy(() => import('views/users/AllUsers')));
const PendingEntries = Loadable(lazy(() => import('views/entries/PendingEntries')));
const ExpiryDashboard = Loadable(lazy(() => import('views/entries/ExpiryDashboard')));
const AddStaff = Loadable(lazy(() => import('views/staff/AddStaff')));
const AllStaff = Loadable(lazy(() => import('views/staff/AllStaff')));
const EditStaff = Loadable(lazy(() => import('views/staff/EditStaff')));
const CompletePayment = Loadable(lazy(() => import('views/entries/CompletePayment')));
const AllInvoices = Loadable(lazy(() => import('views/invoice/AllInvoices')));
const SentInvoices = Loadable(lazy(() => import('views/invoice/SentInvoices')));
const SendInvoice = Loadable(lazy(() => import('views/invoice/SendInvoice')));
const EditInvoice = Loadable(lazy(() => import('views/invoice/EditInvoice')));
const AddExpense = Loadable(lazy(() => import('views/expense/AddExpense')));
const CompletedExpenses = Loadable(lazy(() => import('views/expense/CompletedExpenses')));
const PendingExpenses = Loadable(lazy(() => import('views/expense/PendingExpenses')));
const EditPackage = Loadable(lazy(() => import('views/isps/EditPackage')));
const UpdatePassword = Loadable(lazy(() => import('views/dashboard/Default/UpdatePassword')));
const UpdateProfile = Loadable(lazy(() => import('views/dashboard/Default/UpdateProfile')));
const AddExtraIncome = Loadable(lazy(() => import('views/extra-income/AddExtraIncome')));
const CompletedExtraIncomes = Loadable(lazy(() => import('views/extra-income/CompletedExtraIncomes')));
const PendingExtraIncomes = Loadable(lazy(() => import('views/extra-income/PendingExtraIncomes')));
const EditExtraIncome = Loadable(lazy(() => import('views/extra-income/EditExtraIncome')));
const WhatsAppSettings = Loadable(lazy(() => import('views/settings/WhatsAppSettings')));
const AuditLogs = Loadable(lazy(() => import('views/audit-log/AuditLogs')));
const UserPaymentHistory = Loadable(lazy(() => import('views/users/UserPaymentHistory')));
const MonthlyPL = Loadable(lazy(() => import('views/reports/MonthlyPL')));
const IspBalance = Loadable(lazy(() => import('views/reports/IspBalance')));
const AllComplaints = Loadable(lazy(() => import('views/complaints/AllComplaints')));

// ==============================|| MAIN ROUTING ||============================== //

const checkLogin = (element) => {
    return jwt.getIsLogin() === true ? element : <Navigate to="/login" replace={true} />;
};

const guard = (permission, element) => checkLogin(<PermissionGuard permission={permission}>{element}</PermissionGuard>);

const MainRoutes = {
    path: '/dashboard',
    element: checkLogin(<MainLayout />),
    children: [
        { path: '/dashboard', element: checkLogin(<DashboardDefault />) },

        // Staff (admin-only — no permission guard, just login check)
        { path: '/dashboard/add-staff', element: checkLogin(<AddStaff />) },
        { path: '/dashboard/all-staff', element: checkLogin(<AllStaff />) },
        { path: '/dashboard/edit-staff', element: checkLogin(<EditStaff />) },

        // ISPs
        { path: '/dashboard/add-isp', element: guard('create_isp', <AddISP />) },
        { path: '/dashboard/edit-isp', element: guard('edit_isp', <EditISP />) },
        { path: '/dashboard/all-isps', element: guard('view_isps', <ViewAllISPs />) },

        // Packages
        { path: '/dashboard/add-package', element: guard('create_package', <AddPackage />) },
        { path: '/dashboard/edit-package/:packageId', element: guard('edit_package', <EditPackage />) },
        { path: '/dashboard/all-packages', element: guard('view_packages', <AllPackages />) },

        // Entries
        { path: '/dashboard/create-entry', element: guard('create_entry', <AddEntry />) },
        { path: '/dashboard/edit-entry', element: guard('edit_entry', <EditEntry />) },
        { path: '/dashboard/all-entries', element: guard('view_entries', <AllEntries />) },
        { path: '/dashboard/pending-entries', element: guard('view_entries', <PendingEntries />) },
        { path: '/dashboard/expiry-dashboard', element: guard('view_expiry_radar', <ExpiryDashboard />) },
        { path: '/dashboard/complete-payment/:id', element: guard('complete_payment', <CompletePayment />) },

        // Invoices
        { path: '/dashboard/all-invoices', element: guard('view_invoices', <AllInvoices />) },
        { path: '/dashboard/sent-invoices', element: guard('view_invoices', <SentInvoices />) },
        { path: '/dashboard/send-invoice', element: guard('create_invoice', <SendInvoice />) },
        { path: '/dashboard/edit-invoice', element: guard('edit_invoice', <EditInvoice />) },

        // Users
        { path: '/dashboard/add-user', element: guard('create_user', <AddUser />) },
        { path: '/dashboard/edit-user', element: guard('edit_user', <EditUser />) },
        { path: '/dashboard/all-users', element: guard('view_users', <AllUsers />) },
        { path: '/dashboard/user-payment-history', element: guard('view_user_history', <UserPaymentHistory />) },

        // Expenses
        { path: '/dashboard/add-expense', element: guard('create_expense', <AddExpense />) },
        { path: '/dashboard/all-expenses', element: guard('view_expenses', <CompletedExpenses />) },
        { path: '/dashboard/pending-expenses', element: guard('view_expenses', <PendingExpenses />) },

        // Extra Income
        { path: '/dashboard/add-extra-income', element: guard('create_extra_income', <AddExtraIncome />) },
        { path: '/dashboard/completed-extra-incomes', element: guard('view_extra_income', <CompletedExtraIncomes />) },
        { path: '/dashboard/pending-extra-incomes', element: guard('view_extra_income', <PendingExtraIncomes />) },
        { path: '/dashboard/edit-extra-income', element: guard('edit_extra_income', <EditExtraIncome />) },

        // Profile (always accessible)
        { path: '/dashboard/update-password', element: checkLogin(<UpdatePassword />) },
        { path: '/dashboard/update-profile', element: checkLogin(<UpdateProfile />) },

        // Settings (admin only)
        { path: '/dashboard/settings/whatsapp', element: checkLogin(<WhatsAppSettings />) },

        // Audit Log (admin only)
        { path: '/dashboard/audit-logs', element: guard('view_audit_log', <AuditLogs />) },

        // Reports
        { path: '/dashboard/monthly-pl', element: guard('view_pl_report', <MonthlyPL />) },
        { path: '/dashboard/isp-balance', element: guard('view_isp_balance', <IspBalance />) },

        // Complaints
        { path: '/dashboard/complaints', element: guard('view_complaints', <AllComplaints />) },
    ]
};

export default MainRoutes;
